<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import MetricCard from './overview/MetricCard.vue';
import kanbanClient from 'dashboard/api/kanban/kanbanClient';
import { useAccount } from 'dashboard/composables/useAccount';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const isLoading = ref(true);
const summary = ref({});

const i18nKey = 'OVERVIEW_REPORTS.DEAL_METRICS';

const fmt = v =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v || 0);

const pipelineMetrics = computed(() => {
  const s = summary.value;
  return {
    [t(`${i18nKey}.PIPELINE`)]: fmt(s.active_value),
    [t(`${i18nKey}.CONVERSION_RATE`)]: `${s.conversion_rate ?? 0}%`,
    [t(`${i18nKey}.WON_VALUE`)]: fmt(s.won_value),
  };
});

const alertMetrics = computed(() => {
  const s = summary.value;
  return {
    [t(`${i18nKey}.BOTTLENECK`)]: s.bottleneck_stage || '—',
    [t(`${i18nKey}.STALE_DEALS`)]: s.stale_count ?? 0,
    [t(`${i18nKey}.AVG_CYCLE`)]: `${s.avg_cycle_days ?? 0}d`,
  };
});

const fetchData = async () => {
  isLoading.value = true;
  try {
    const { data } = await kanbanClient.getDealSummary();
    summary.value = data;
  } catch {
    summary.value = {};
  } finally {
    isLoading.value = false;
  }
};

const navigateToReports = () => {
  router.push(accountScopedRoute('kanban_reports'));
};

const { startRefetching } = useLiveRefresh(fetchData);

onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="flex flex-col items-center md:flex-row gap-4">
    <div class="flex-1 w-full max-w-full md:w-[50%] md:max-w-[50%]">
      <MetricCard
        :header="t(`${i18nKey}.HEADER`)"
        :is-loading="isLoading"
        :loading-message="t(`${i18nKey}.LOADING_MESSAGE`)"
      >
        <template #control>
          <button
            class="text-xs text-n-brand hover:underline cursor-pointer"
            @click="navigateToReports"
          >
            {{ t(`${i18nKey}.VIEW_ALL`) }}
          </button>
        </template>
        <div
          v-for="(metric, name, index) in pipelineMetrics"
          :key="index"
          class="flex-1 min-w-0 pb-2"
        >
          <h3 class="text-base text-n-slate-11">
            {{ name }}
          </h3>
          <p class="text-n-slate-12 text-3xl mb-0 mt-1">
            {{ metric }}
          </p>
        </div>
      </MetricCard>
    </div>
    <div class="flex-1 w-full max-w-full md:w-[50%] md:max-w-[50%]">
      <MetricCard
        :header="t(`${i18nKey}.ALERTS_HEADER`)"
        :is-loading="isLoading"
        :loading-message="t(`${i18nKey}.LOADING_MESSAGE`)"
      >
        <div
          v-for="(metric, name, index) in alertMetrics"
          :key="index"
          class="flex-1 min-w-0 pb-2"
        >
          <h3 class="text-base text-n-slate-11">
            {{ name }}
          </h3>
          <p class="text-n-slate-12 text-3xl mb-0 mt-1">
            {{ metric }}
          </p>
        </div>
      </MetricCard>
    </div>
  </div>
</template>
